import { useState } from 'react';
import {
  Alert,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogContentText,
  DialogActions,
  FormHelperText,
  TextField,
  ToggleButton,
  ToggleButtonGroup,
  Tooltip,
  Button,
} from '@mui/material';
import sendNodeCommand from 'store/sendNodeCommand';
import { IWorkerSummary } from 'types/INodeAgent';
import { buildSoftwareRef, isSoftwareRef, splitSoftwareRef } from 'utils/softwareRef';
import {
  DEFAULT_MAX_TASKS_PER_PROCESS,
  MIN_REPLACE_PYTAILOR_VERSION,
  agentSupportsOvershoot,
  agentSupportsReplace,
  defaultReplacementBudget,
} from 'utils/workerGroup';
import { errorMessage } from './errorMessage';
import { SlotBudget, fitsSlotBudget, slotBudgetHint } from 'utils/slotBudget';

export type UpgradeMode = 'rolling' | 'restart';

interface UpgradeWorkerDialogProps {
  nodeAgentId: string;
  instanceId: string;
  workerName: string;
  currentPackageName?: string;
  currentVersion?: string;
  currentSoftware?: string;
  currentSoftwareRef?: string;
  // What an in-place upgrade interrupts when it restarts the worker.
  runningTasks?: number;
  // The row itself and the members of its group: what a rolling replacement
  // is composed from and what it retires. Without them only the in-place
  // upgrade is offered.
  worker?: IWorkerSummary;
  groupMembers?: IWorkerSummary[];
  agentPytailorVersion?: string | null;
  slotBudget?: SlotBudget | null;
  onClose: () => void;
  onConfirm: (mode: UpgradeMode) => void;
}

export default function UpgradeWorkerDialog({
  nodeAgentId,
  instanceId,
  workerName,
  currentPackageName,
  currentVersion,
  currentSoftware,
  currentSoftwareRef,
  runningTasks = 0,
  worker,
  groupMembers = [],
  agentPytailorVersion,
  slotBudget,
  onClose,
  onConfirm,
}: UpgradeWorkerDialogProps) {
  // A workdir's source is fixed at start: a Depot worker moves between releases
  // of the same name, so only the part after the name is editable.
  const depot = splitSoftwareRef(currentSoftware || currentSoftwareRef || '');
  const isDepot = Boolean(currentSoftware || currentSoftwareRef);
  const [newVersion, setNewVersion] = useState(isDepot ? depot.part : currentVersion || '');

  const rollingDisabledReason = !worker
    ? 'Not available for this worker.'
    : !agentSupportsReplace(agentPytailorVersion)
      ? `Needs a node agent on pytailor ${MIN_REPLACE_PYTAILOR_VERSION} or newer (this one runs ${agentPytailorVersion || 'an unknown version'}).`
      : null;
  const [mode, setMode] = useState<UpgradeMode>(rollingDisabledReason === null ? 'rolling' : 'restart');
  const members = groupMembers.length > 0 ? groupMembers : worker ? [worker] : [];
  const [budget, setBudget] = useState(String(defaultReplacementBudget(members) || worker?.slots || 1));

  const trimmed = newVersion.trim();
  const newRef = isDepot ? buildSoftwareRef(depot.name, trimmed) : '';
  const refInvalid = isDepot && trimmed !== '' && !isSoftwareRef(newRef);
  const refMissing = isDepot && trimmed === '';
  const budgetInvalid = mode === 'rolling' && !(parseInt(budget, 10) > 0);
  const budgetFits =
    mode !== 'rolling' ||
    fitsSlotBudget(slotBudget ?? null, parseInt(budget, 10) || 0, defaultReplacementBudget(members));
  const budgetHint = slotBudgetHint(slotBudget ?? null, parseInt(budget, 10) || 0, defaultReplacementBudget(members));

  const groupRunningTasks = members.reduce((sum, w) => sum + (w.running_tasks ?? 0), 0);

  const [error, setError] = useState<string | null>(null);
  const [isSubmitting, setIsSubmitting] = useState(false);

  const rollingParams = (): Record<string, unknown> => {
    if (!worker) throw new Error('rolling replacement needs the worker summary');
    const packageBase = (currentPackageName ?? worker.package_name ?? 'pytailor').split('==')[0];
    return {
      ...(isDepot
        ? { software_ref: newRef }
        : {
            package_name: trimmed ? `${packageBase}==${trimmed}` : packageBase,
            ...(worker.python_version && { python_version: worker.python_version }),
          }),
      slots: parseInt(budget, 10),
      ...((worker.overshoot ?? 0) > 0 &&
        agentSupportsOvershoot(agentPytailorVersion) && { overshoot: worker.overshoot }),
      sleep: worker.sleep ?? 3,
      worker_name: worker.name,
      ...(worker.project_id_filter?.length && { project_id_filter: worker.project_id_filter }),
      ...(worker.capabilities?.length && { capabilities: worker.capabilities }),
      max_tasks_per_process: worker.max_tasks_per_process ?? DEFAULT_MAX_TASKS_PER_PROCESS,
      replace: true,
    };
  };

  const handleConfirm = async () => {
    if (isSubmitting) return;
    setIsSubmitting(true);
    setError(null);
    try {
      if (mode === 'rolling') {
        await sendNodeCommand(nodeAgentId, 'start_worker', rollingParams());
      } else {
        await sendNodeCommand(
          nodeAgentId,
          'upgrade_worker',
          isDepot
            ? { instance_id: instanceId, software_ref: newRef }
            : { instance_id: instanceId, package_version: trimmed || undefined },
        );
      }
      onConfirm(mode);
      onClose();
    } catch (err) {
      console.error('Failed to upgrade worker:', err);
      setError(`Failed to upgrade worker: ${await errorMessage(err)}`);
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleClose = () => {
    if (isSubmitting) return;
    setError(null);
    onClose();
  };

  return (
    <Dialog open onClose={handleClose} maxWidth="sm" fullWidth>
      <DialogTitle>Upgrade Worker</DialogTitle>
      <DialogContent>
        <DialogContentText gutterBottom>
          Upgrade worker <strong>{workerName}</strong>
          {isDepot ? (
            <>
              {' '}
              on <strong>{currentSoftware || currentSoftwareRef}</strong>
            </>
          ) : currentPackageName ? (
            <>
              {' '}
              running <strong>{currentPackageName}</strong>
              {currentVersion && (
                <>
                  {' '}
                  <strong>{currentVersion}</strong>
                </>
              )}
            </>
          ) : null}
          .
        </DialogContentText>
        <ToggleButtonGroup
          exclusive
          value={mode}
          onChange={(_, value: UpgradeMode | null) => {
            if (value) setMode(value);
          }}
          size="small"
          aria-label="Upgrade mode"
          sx={{ mt: 1 }}
        >
          <Tooltip title={rollingDisabledReason ?? ''}>
            <span>
              <ToggleButton value="rolling" disabled={rollingDisabledReason !== null}>
                Rolling replace
              </ToggleButton>
            </span>
          </Tooltip>
          <ToggleButton value="restart">Restart in place</ToggleButton>
        </ToggleButtonGroup>
        <FormHelperText sx={{ mb: 1 }}>
          {mode === 'rolling'
            ? `Starts a new worker on the new version beside this one and retires this one; slots move over as its ${groupRunningTasks} running ${groupRunningTasks === 1 ? 'task finishes' : 'tasks finish'}. Nothing is killed.`
            : 'Stops this worker, upgrades it in place and restarts it. Running tasks are interrupted.'}
        </FormHelperText>
        {mode === 'restart' && runningTasks > 0 && (
          <Alert severity="warning" sx={{ mt: 1 }}>
            {runningTasks} running {runningTasks === 1 ? 'task' : 'tasks'} will be interrupted: the upgrade restarts the
            worker immediately without draining. Interrupted tasks are flagged as lost and only run again after being
            reset.
          </Alert>
        )}
        {mode === 'rolling' && members.length > 1 && (
          <Alert severity="info" sx={{ mt: 1 }}>
            Replaces all {members.length} workers of this group:{' '}
            {members
              .map((w) => `${w.instance_id} (${w.status.toLowerCase()}, ${w.running_tasks ?? 0} running)`)
              .join(', ')}
            .
          </Alert>
        )}
        {isDepot ? (
          <>
            <TextField label="Release" value={depot.name} fullWidth margin="normal" disabled />
            <TextField
              label="New version or alias"
              value={newVersion}
              onChange={(e) => setNewVersion(e.target.value)}
              fullWidth
              margin="normal"
              placeholder="e.g. 0.31.0 or 0-stable"
              error={refInvalid}
              helperText={
                refInvalid
                  ? 'Not a version or alias of this release (e.g. 0.31.0, 0-stable, 0-next).'
                  : refMissing
                    ? 'A version (0.31.0) or an alias (0-stable, 0-next) of this release.'
                    : `${mode === 'rolling' ? 'Starts the new worker on' : 'Upgrades to'} ${newRef}. An alias is frozen to a pin when it runs.`
              }
            />
          </>
        ) : (
          <>
            <TextField label="Package Name" value={currentPackageName ?? ''} fullWidth margin="normal" disabled />
            <TextField
              label="New Version"
              value={newVersion}
              onChange={(e) => setNewVersion(e.target.value)}
              fullWidth
              margin="normal"
              placeholder="e.g. 0.12.0"
              helperText={
                mode === 'rolling'
                  ? 'Leave empty to start the new worker on the latest version.'
                  : 'Leave empty to upgrade to the latest version.'
              }
            />
          </>
        )}
        {mode === 'rolling' && (
          <TextField
            label="Budget (slots)"
            type="number"
            value={budget}
            onChange={(e) => setBudget(e.target.value)}
            fullWidth
            margin="normal"
            error={budgetInvalid || !budgetFits}
            slotProps={{ htmlInput: { min: 1 } }}
            helperText={
              budgetHint
                ? `Slots the new worker holds once the replaced workers have drained; the group's own slots transfer. ${budgetHint}`
                : 'Slots the new worker holds once the replaced workers have drained. Defaults to what the group holds today.'
            }
          />
        )}
        {error && (
          <Alert severity="error" sx={{ mt: 2 }}>
            {error}
          </Alert>
        )}
      </DialogContent>
      <DialogActions>
        <Button onClick={handleClose} disabled={isSubmitting}>
          Cancel
        </Button>
        <Button
          onClick={handleConfirm}
          variant="contained"
          color="primary"
          disabled={isSubmitting || refInvalid || refMissing || budgetInvalid || !budgetFits}
        >
          {isSubmitting
            ? mode === 'rolling'
              ? 'Starting…'
              : 'Upgrading…'
            : mode === 'rolling'
              ? 'Replace'
              : 'Upgrade'}
        </Button>
      </DialogActions>
    </Dialog>
  );
}
